import { useCallback, useState } from "react";

/**
 * Sign-in with an emailed code, shared by staff web (`@clerk/react`) and staff
 * Android (`@clerk/expo`). Both SDKs expose the same `SignInFuture` and
 * `SignUpFuture` resources; only the parts used here are typed, so staff-core
 * needs no Clerk import. A new email becomes a sign-up, because staff often
 * join by invite before they have an account.
 */

type ClerkResult = Promise<{ error: unknown }>;

export type EmailCodeSignIn = {
	status: string;
	emailCode: {
		sendCode(params: { emailAddress: string }): ClerkResult;
		verifyCode(params: { code: string }): ClerkResult;
	};
	finalize(): ClerkResult;
};

export type EmailCodeSignUp = {
	status: string | null;
	create(params: { emailAddress: string }): ClerkResult;
	verifications: {
		sendEmailCode(): ClerkResult;
		verifyEmailCode(params: { code: string }): ClerkResult;
	};
	finalize(): ClerkResult;
};

/** i18n keys in the `auth` namespace, or `errors:RATE_LIMITED`. */
export type EmailCodeError =
	| "emailInvalid"
	| "emailUnavailable"
	| "codeIncorrect"
	| "codeExpired"
	| "signInFailed"
	| "errors:RATE_LIMITED";

/** The first Clerk API error code, for branching. Messages are never shown as is. */
export function clerkErrorCode(error: unknown): string | undefined {
	if (!error || typeof error !== "object") return undefined;
	const errors = (error as { errors?: { code?: string }[] }).errors;
	return errors?.[0]?.code ?? (error as { code?: string }).code;
}

export function toEmailCodeError(error: unknown): EmailCodeError {
	switch (clerkErrorCode(error)) {
		case "form_identifier_invalid":
			return "emailInvalid";
		// The address already passed our format check, so Clerk refusing it usually
		// means email sign-in is switched off for the instance.
		case "form_param_format_invalid":
		case "form_param_unknown":
		case "strategy_for_user_invalid":
		case "form_identifier_not_allowed":
			return "emailUnavailable";
		case "form_code_incorrect":
			return "codeIncorrect";
		case "verification_expired":
			return "codeExpired";
		case "too_many_requests":
			return "errors:RATE_LIMITED";
		default:
			return "signInFailed";
	}
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type EmailCodeFlow = {
	step: "email" | "code";
	email: string;
	pending: boolean;
	error: EmailCodeError | null;
	sendCode(email: string): Promise<void>;
	verifyCode(code: string): Promise<void>;
	resend(): Promise<void>;
	/** Back to the email step. */
	restart(): void;
};

export function useEmailCodeFlow(
	signIn: EmailCodeSignIn | null,
	signUp: EmailCodeSignUp | null,
): EmailCodeFlow {
	const [step, setStep] = useState<"email" | "code">("email");
	const [mode, setMode] = useState<"signIn" | "signUp">("signIn");
	const [email, setEmail] = useState("");
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<EmailCodeError | null>(null);

	const run = useCallback(async (work: () => Promise<EmailCodeError | null>) => {
		setPending(true);
		setError(null);
		try {
			setError(await work());
		} catch (thrown) {
			setError(toEmailCodeError(thrown));
		} finally {
			setPending(false);
		}
	}, []);

	const sendCode = useCallback(
		(address: string) =>
			run(async () => {
				const emailAddress = address.trim().toLowerCase();
				if (!EMAIL.test(emailAddress)) return "emailInvalid";
				if (!signIn) return "signInFailed";
				setEmail(emailAddress);
				const sent = await signIn.emailCode.sendCode({ emailAddress });
				if (!sent.error) {
					setMode("signIn");
					setStep("code");
					return null;
				}
				if (clerkErrorCode(sent.error) !== "form_identifier_not_found" || !signUp) {
					return toEmailCodeError(sent.error);
				}
				const created = await signUp.create({ emailAddress });
				if (created.error) return toEmailCodeError(created.error);
				const mailed = await signUp.verifications.sendEmailCode();
				if (mailed.error) return toEmailCodeError(mailed.error);
				setMode("signUp");
				setStep("code");
				return null;
			}),
		[run, signIn, signUp],
	);

	const verifyCode = useCallback(
		(code: string) =>
			run(async () => {
				const trimmed = code.replace(/\s/g, "");
				if (mode === "signIn") {
					if (!signIn) return "signInFailed";
					const verified = await signIn.emailCode.verifyCode({ code: trimmed });
					if (verified.error) return toEmailCodeError(verified.error);
					if (signIn.status !== "complete") return "signInFailed";
					const done = await signIn.finalize();
					return done.error ? toEmailCodeError(done.error) : null;
				}
				if (!signUp) return "signInFailed";
				const verified = await signUp.verifications.verifyEmailCode({ code: trimmed });
				if (verified.error) return toEmailCodeError(verified.error);
				if (signUp.status !== "complete") return "signInFailed";
				const done = await signUp.finalize();
				return done.error ? toEmailCodeError(done.error) : null;
			}),
		[run, signIn, signUp, mode],
	);

	return {
		step,
		email,
		pending,
		error,
		sendCode,
		verifyCode,
		resend: () => sendCode(email),
		restart: () => {
			setStep("email");
			setError(null);
		},
	};
}
